import type { RcScopeType } from '../shipments/shipments-types.js';
import type {
  CompanyReceiverType,
  CounterpartyRef,
  CounterpartySearchSource,
  CounterpartySelection,
  PartyAddressSnapshot,
  RegulatoryIdentity,
} from '../counterparties/counterparties.dto.js';

export const companyReceiverStatusValues = ['ACTIVE', 'INACTIVE'] as const;
export type CompanyReceiverStatus = (typeof companyReceiverStatusValues)[number];

export interface CompanyReceiverDirectoryRecord {
  id: string;
  ownerCompanyId: string;
  receiverDirectoryScopeId: string;
  displayName: string;
  receiverType: CompanyReceiverType;
  referenceCode?: string | null;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'COMPANY_RECEIVER' }>;
  address?: PartyAddressSnapshot | null;
  status: CompanyReceiverStatus;
  createdAt: string;
  updatedAt: string;
}

export interface InternalRcScopeDirectoryRecord {
  id: string;
  companyId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  displayName: string;
  companyName: string | null;
  storeName: string | null;
  locationName: string | null;
  referenceCode?: string | null;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'INTERNAL_RC_SCOPE' }>;
  address?: PartyAddressSnapshot | null;
  isActive: boolean;
  updatedAt: string;
}

export interface GlobalRegulatoryEntityDirectoryRecord {
  id: string;
  displayName: string;
  referenceCode?: string | null;
  regulatoryIdentity: RegulatoryIdentity;
  receiverType?: CompanyReceiverType | null;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'REGULATORY_ENTITY' }>;
  address?: PartyAddressSnapshot | null;
  updatedAt: string;
}

interface ReceiverDirectoryItemBase {
  id: string;
  source: CounterpartySearchSource;
  displayName: string;
  companyName: string | null;
  referenceCode?: string | null;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  address?: PartyAddressSnapshot | null;
  isActive: boolean;
  duplicateWarning?: string | null;
}

export interface InternalRcScopeReceiverDirectoryItem extends ReceiverDirectoryItemBase {
  kind: 'INTERNAL_RC_SCOPE';
  source: 'internal';
  companyId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  storeName: string | null;
  locationName: string | null;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'INTERNAL_RC_SCOPE' }>;
  selection: Extract<CounterpartySelection, { kind: 'INTERNAL_RC_SCOPE' }>;
}

export interface CompanyReceiverDirectoryItem extends ReceiverDirectoryItemBase {
  kind: 'COMPANY_RECEIVER';
  source: 'company';
  companyReceiverId: string;
  receiverType: CompanyReceiverType;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'COMPANY_RECEIVER' }>;
  selection: Extract<CounterpartySelection, { kind: 'COMPANY_RECEIVER' }>;
}

export interface RegulatoryEntityDirectoryItem extends ReceiverDirectoryItemBase {
  kind: 'REGULATORY_ENTITY';
  source: 'global';
  companyName: null;
  regulatoryEntityId: string;
  receiverType?: CompanyReceiverType | null;
  counterpartyRef: Extract<CounterpartyRef, { kind: 'REGULATORY_ENTITY' }>;
  selection: Extract<CounterpartySelection, { kind: 'REGULATORY_ENTITY' }>;
}

export type ReceiverDirectoryItem =
  | InternalRcScopeReceiverDirectoryItem
  | CompanyReceiverDirectoryItem
  | RegulatoryEntityDirectoryItem;

export interface SearchReceiverDirectoryRequest {
  companyId: string;
  query?: string;
  limit?: number;
  excludeCounterpartyRef?: CounterpartyRef;
}

export interface SearchReceiverDirectoryResponse {
  items: ReceiverDirectoryItem[];
  canQuickCreateCompanyReceiver: boolean;
}

export interface ReceiverDirectorySnapshotResponse {
  receiverDirectoryScopeId: string;
  internalRcScopes: InternalRcScopeDirectoryRecord[];
  companyReceivers: CompanyReceiverDirectoryRecord[];
  globalRegulatoryEntities: GlobalRegulatoryEntityDirectoryRecord[];
}

export interface CreateCompanyReceiverRequest {
  companyId: string;
  displayName: string;
  receiverType: CompanyReceiverType;
  referenceCode?: string | null;
  regulatoryIdentity: RegulatoryIdentity;
  address?: PartyAddressSnapshot | null;
}

export interface CreateCompanyReceiverResponse {
  companyReceiver: CompanyReceiverDirectoryRecord;
  directoryItem: CompanyReceiverDirectoryItem;
}

export interface UpdateCompanyReceiverRequest {
  companyId: string;
  companyReceiverId: string;
  displayName?: string;
  receiverType?: CompanyReceiverType;
  referenceCode?: string | null;
  regulatoryIdentity?: RegulatoryIdentity;
  address?: PartyAddressSnapshot | null;
  status?: CompanyReceiverStatus;
}

export interface UpdateCompanyReceiverResponse {
  companyReceiver: CompanyReceiverDirectoryRecord;
  directoryItem: CompanyReceiverDirectoryItem;
}
